import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ReportService } from '../../services/report.service';
import { ActivatedRoute } from '@angular/router';
import { DeceasedReport, ReportResultStatus, ReportStatus, ReportType } from '../../../../models/report.model';
import { getDateDaysAgo } from '../../../shared/utils/date.utils';
import { exhaustMap, interval, startWith, Subscription,  takeWhile } from 'rxjs';
import { primaryReportExample } from '../../../../examples/primary-report.example';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-report-mini',
  templateUrl: './report-primary.component.html',
  styleUrls: ['./report-primary.component.scss']
})
export class ReportPrimaryComponent implements OnInit, OnDestroy {

  public report: Extract<DeceasedReport, { type: ReportType.PRIMARY }> | null = null;
  public isLoading = true;

  private _reportId ='';
  private _subscription!: Subscription;
  public useMockData = false;
  public reportDeleyed = false;

  get progress(): number {
    if (!this.report?.lookup) {
      return 0;
    }
    const lookups = Object.values(this.report.lookup);
    const completed = lookups.filter((lookup: any) =>
      ['PROCESSED', 'SKIPPED'].includes(lookup.status)
    ).length;
    return Math.round((completed / lookups.length) * 100);
  }


  constructor(
      private _reportService:ReportService,
      private _route:ActivatedRoute,
      @Inject(PLATFORM_ID) private platformId: Object
  ) {
  }


  ngOnInit() {
    this.useMockData = this._route.snapshot.data['mockData'];
    this._reportId = this._route.snapshot.paramMap.get('id') ?? '';

    if (!this._reportId) {
      this.useMockData = true;
    }

    if (this.useMockData) {
      this.report = primaryReportExample;
      this._reportId = '';
      this.isLoading = false;
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this._subscription = interval(3000)
        .pipe(
          startWith(0),
          exhaustMap(() => this._reportService.getReport(this._reportId)),
          takeWhile(
            response => response.data.status !== ReportStatus.PROCESSED,
            true
          )
        )
        .subscribe({
          next: response => {
            this.isLoading = false;

            if (response.data.type !== ReportType.PRIMARY) {
              this.report = null;
              this._unsubscribe();
              return;
            }

            this.report = response.data;
            this.reportDeleyed = this.isReportDelayed(response.data.createdAt);

            if (this.report.status === ReportStatus.PROCESSED) {
              this._unsubscribe();
            }
          },
          error: error => {
            console.error(error);
            this.report = null;
            this.isLoading = false;
            this._unsubscribe();
          }
        });
    }
  }

  public getDBStatusDate(date: Date): Date | null {
      return getDateDaysAgo(date, 1);
  }


  public generatePdf() {

    if (isPlatformBrowser(this.platformId) && this.report) {
      this._reportService.getPrimaryReportPDF(this.report, this._reportId);
    }


  }

  public mapReportResult(result: ReportResultStatus) {
   return this._reportService.mapPrimaryReportResult(result);
  }

  ngOnDestroy() {
    this._unsubscribe();
  }

  private isReportDelayed(createdAt: Date | string): boolean {
    const createdAtDate = new Date(createdAt);

    if (isNaN(createdAtDate.getTime())) {
      return false;
    }
    return Date.now() - createdAtDate.getTime() > 30 * 60 * 1000;
  }


  private _unsubscribe() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
